/*
  Реквизиты взыскателя: то, без чего заявление вернут.

  Недостающее считает сервер - тем же кодом, каким проверяет шаблонизатор.
  Список на клиенте не пересобирается: это был бы второй ответ на вопрос «чего
  не хватает», и он разошёлся бы с первым при первом же новом требовании.

  Ширина каждого поля объявлена рядом с ним и означает длину значения: ОГРН -
  тринадцать цифр, адрес - строка, должность - слово.

  14.09.2026, правовая вычитка. Прибавились две вещи, обе - из редакции
  заявления от ведущего юриста: чем подписывается заявление (от этого зависит
  перечень приложений) и территориальные органы, которым суд шлёт запрос об
  идентификаторах должника. Органы вводятся один раз: вы работаете в своём
  регионе, и меняются они редко.

  🔴 23.09.2026, решение владельца: группы разложены вложенными вкладками. До
  этого восемнадцать полей и переключатель шли одной простынёй в пять разделов
  подряд, и до запросов суда надо было доезжать прокруткой. Тот же приём, каким
  09.09.2026 разложили сами настройки, - и та же причина.

  Из вкладок следует устройство состояния. Содержимое невыбранной вкладки не
  рисуется (`Tabs.jsx`), поэтому собрать форму из `FormData` при отправке больше
  нельзя: полей чужих вкладок в разметке нет вовсе, и сохранение обнулило бы
  всё, кроме открытой вкладки. Значения лежат одним объектом, поля управляемые,
  кнопка сохраняет все вкладки разом.
*/

import { useEffect, useState } from 'react'

import { dateTime } from '../api/format.js'
import { LockedForm } from './AccessContext.jsx'
import { Field, Switch, Tag } from './Common.jsx'
import Tabs, { TabPanel } from './Tabs.jsx'

const CREDITOR = [
  { key: 'ogrn', label: 'ОГРН', width: 's' },
  { key: 'address', label: 'Адрес организации', width: 'l' },
  // 05.10.2026, решение владельца: банковские реквизиты взыскателя - четырьмя
  // полями вместо одной строки, в которой на деле лежал только номер счёта. Суду
  // они печатаются полностью, как в редакции ведущего юриста: «р/с … в …, к/с …,
  // БИК …». Для судебного приказа не обязательны.
  {
    key: 'creditor_settlement_account',
    label: 'Расчётный счёт',
    width: 'm',
    hint: 'Не обязательно для судебного приказа. Без счёта банковские реквизиты в заявление не попадут.',
  },
  {
    key: 'creditor_bank_name',
    label: 'Банк',
    width: 'm',
    hint: 'Так, как пишется после «в»: «в Кемеровском отделении ПАО Сбербанк».',
  },
  { key: 'creditor_bank_bic', label: 'БИК банка', width: 's' },
  { key: 'creditor_correspondent_account', label: 'Корреспондентский счёт', width: 'm' },
  { key: 'phone', label: 'Телефон', width: 's' },
  { key: 'email', label: 'Почта для суда', width: 'm' },
]

const SIGNATORY = [
  { key: 'signatory_name', label: 'Кто подписывает заявления', width: 'm' },
  { key: 'signatory_position', label: 'Должность подписанта', width: 's' },
  {
    key: 'signatory_basis',
    label: 'Основание полномочий',
    width: 's',
    hint: 'Устав или доверенность - слово в слово, как в самом документе.',
  },
]

// 23.09.2026: из этих полей собирается код оплаты на странице должника (ГОСТ Р
// 56042-2014), потому что банк ищет счёт и БИК каждый в своём поле. Счёт для
// приёма платежей может отличаться от того, что взыскатель называет суду, -
// поэтому у взыскателя свои поля (05.10.2026).
const PAYMENT = [
  { key: 'settlement_account', label: 'Расчётный счёт', width: 'm' },
  { key: 'bank_name', label: 'Банк', width: 'm' },
  { key: 'bank_bic', label: 'БИК банка', width: 's' },
  { key: 'correspondent_account', label: 'Корреспондентский счёт', width: 'm' },
  { key: 'kpp', label: 'КПП', width: 's' },
]

// 15.09.2026, указание владельца: копия лицензии на управление домами
// прикладывается к каждому заявлению, и в перечне приложений она называется
// номером и датой. Оставить пустым можно - тогда пункт назовёт лицензию без
// реквизитов, и подаче это не помешает.
const LICENSE = [
  { key: 'license_number', label: 'Номер лицензии', width: 's', type: 'text' },
  { key: 'license_date', label: 'Дата выдачи', width: 's', type: 'date' },
]

const AUTHORITIES = [
  {
    key: 'inquiry_pension_fund',
    label: 'Отделение Фонда пенсионного и социального страхования',
    width: 'l',
  },
  { key: 'inquiry_tax_service', label: 'Управление налоговой службы', width: 'l' },
  { key: 'inquiry_migration', label: 'Отдел по вопросам миграции', width: 'l' },
]

const SIGNATORY_KIND = [
  { value: false, title: 'руководитель по уставу' },
  { value: true, title: 'представитель по доверенности' },
]

/*
  Вкладки идут в порядке «чем чаще трогают, тем левее»: шапка заявления и
  подписант меняются при смене руководителя, лицензия и территориальные органы -
  раз в несколько лет.
*/
const GROUPS = [
  {
    key: 'creditor',
    title: 'Взыскатель',
    fields: CREDITOR,
    note: 'Наименование и ИНН лежат на вкладке «Организация» - здесь остальное, что суд видит в шапке заявления.',
  },
  {
    key: 'signatory',
    title: 'Подписант',
    fields: SIGNATORY,
    note: 'От того, чем подтверждены полномочия, зависит перечень приложений к заявлению.',
  },
  {
    key: 'payment',
    title: 'Оплата по коду',
    fields: PAYMENT,
    note:
      'Из них собирается код оплаты на странице должника - тот же, что печатают на квитанциях. ' +
      'Человек наводит камеру, и получатель, сумма и назначение подставляются сами. Пока хотя бы ' +
      'одно поле пусто, код не показывается, а страница предлагает обычный перевод. Счёт может ' +
      'отличаться от счёта на вкладке «Взыскатель» - в суд идёт тот.',
  },
  {
    key: 'license',
    title: 'Лицензия',
    fields: LICENSE,
    note:
      'Копия лицензии на управление многоквартирными домами прикладывается к каждому заявлению. ' +
      'Оставить пустым можно - тогда в перечне приложений лицензия будет названа без номера и ' +
      'даты, и подаче это не помешает.',
  },
  {
    key: 'inquiry',
    title: 'Запросы суда',
    fields: AUTHORITIES,
    note:
      'Куда суд направит запрос об идентификаторах должника. Заполняется один раз. Если оставить ' +
      'пустым, заявление попросит суд запросить сведения в Фонде пенсионного и социального ' +
      'страхования, налоговых органах и органах внутренних дел без указания конкретных ' +
      'подразделений - подаче это не мешает.',
  },
]

const ALL = GROUPS.flatMap((group) => group.fields)

/*
  `problems` - причины отказа по полям (`fieldErrors`). Поле чужой вкладки в разметке не
  рисуется, поэтому причина под ним осталась бы невидимой: «отмечено под полями», а под
  полями пусто (аудит 26.10.2026, причина 6). Отказ переводит на первую вкладку с
  ошибкой и ставит знак на каждую такую вкладку.
*/
export default function SettingsRequisites({
  requisites,
  onSave,
  problems = {},
  saving = false,
}) {
  const [tab, setTab] = useState(GROUPS[0].key)
  const marked = GROUPS.filter((group) => group.fields.some(({ key }) => problems[key]))
  useEffect(() => {
    if (marked.length) setTab(marked[0].key)
    // Переключение - реакция на НОВЫЙ отказ: ручной выбор вкладки потом не перебивается.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problems])
  const tabs = GROUPS.map((group) => ({
    ...group,
    badge: marked.includes(group) ? '!' : null,
    badgeTitle: 'На этой вкладке есть поле, которое не принято',
  }))
  const [values, setValues] = useState(() => ({
    ...Object.fromEntries(ALL.map(({ key }) => [key, requisites[key] ?? ''])),
    signatory_by_proxy: Boolean(requisites.signatory_by_proxy),
  }))

  const byProxy = values.signatory_by_proxy
  const set = (key, value) => setValues((current) => ({ ...current, [key]: value }))

  return (
    <>
      {/*
        Чего не хватает - ВЫШЕ вкладок, а не внутри одной из них: недостающее
        поле может лежать на любой, и человек, открывший «Запросы суда», иначе
        не узнал бы, что заявление вернут из-за пустого основания полномочий.
      */}
      {requisites.missing.length > 0 ? (
        <div className="notice notice--warn">
          <div className="notice__what">Не заполнено: {requisites.missing.join(', ')}.</div>
          <div className="notice__do">
            Заявление с пропусками в реквизитах суд вернёт - заполните до формирования пакета.
          </div>
        </div>
      ) : (
        <Tag kind="calm">реквизитов достаточно для подачи</Tag>
      )}

      {/* Вкладки - ВНЕ формы: при неоплаченной лицензии форма вся серая, а читать
          остальные вкладки по-прежнему нужно. */}
      <Tabs tabs={tabs} active={tab} onChange={setTab} nested />

      <LockedForm
        busy={saving}
        onSubmit={(event) => {
          event.preventDefault()
          // Пустое поле уходит как `null`, а не как пустая строка: сервер
          // отличает «не заполнено» от «заполнено ничем» и считает по первому
          // список недостающего.
          const filled = ALL.map(({ key }) => [key, values[key] || null])
          onSave({ ...Object.fromEntries(filled), signatory_by_proxy: byProxy })
        }}
      >
        {GROUPS.map((group) => (
          <TabPanel key={group.key} tabKey={group.key} active={tab}>
            <p className="muted tabs__note">{group.note}</p>

            {group.fields.map(({ key, label, width, hint, type }) => (
              <Field
                key={key}
                label={label}
                width={width}
                hint={hint}
                error={problems[key]}
                errorId={`requisites-error-${key}`}
              >
                <input
                  className="field__input"
                  name={key}
                  type={type}
                  value={values[key]}
                  aria-invalid={problems[key] ? 'true' : undefined}
                  aria-describedby={problems[key] ? `requisites-error-${key}` : undefined}
                  onChange={(event) => set(key, event.target.value)}
                />
              </Field>
            ))}

            {/* Переключатель стоит на своей вкладке и только на ней: это не
                поле ввода, и общим перебором полей он не рисуется. */}
            {group.key === 'signatory' ? (
              <Field
                label="Заявление подписывает"
                hint={
                  byProxy
                    ? 'В приложениях к каждому заявлению будут полномочия и диплом представителя.'
                    : 'Приложений к заявлению меньше: доверенность и диплом не нужны.'
                }
              >
                <Switch
                  value={byProxy}
                  onChange={(value) => set('signatory_by_proxy', value)}
                  options={SIGNATORY_KIND}
                />
              </Field>
            ) : null}
          </TabPanel>
        ))}

        {/* Пояснение стоит ПЕРЕД кнопкой, а не под ней: человек, заполнивший
            три вкладки, должен узнать, что сохранятся все, до нажатия, а не
            после. */}
        <p className="muted tabs__note">
          Сохраняются все вкладки разом - переключаться между ними можно, введённое не теряется.
        </p>
        <button className="button" type="submit">
          {saving ? 'Сохраняем…' : 'Сохранить реквизиты'}
        </button>
        {requisites.updated_at ? (
          <div className="muted">Последнее изменение: {dateTime(requisites.updated_at)}</div>
        ) : null}
      </LockedForm>
    </>
  )
}
